import { useState, useCallback, useMemo } from 'react';

interface FormData {
  nombre: string;
  descripcion: string;
}

export interface UseAddCategoryModalReturn {
  formData: FormData;
  loading: boolean;
  error: string | null;
  isFormValid: boolean;
  handleFormChange: (field: string, value: string) => void;
  handleSave: (onSave: (data: FormData) => Promise<void>) => Promise<void>;
  handleClose: (onClose: () => void) => void;
  resetForm: () => void;
}

export const useAddCategoryModal = (): UseAddCategoryModalReturn => {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    descripcion: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFormChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({ nombre: '', descripcion: '' });
    setError(null);
  }, []);

  const handleSave = useCallback(
    async (onSave: (data: FormData) => Promise<void>) => {
      // Validation
      if (!formData.nombre.trim()) {
        setError('El nombre es requerido');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        await onSave(formData);

        // Reset form on success
        resetForm();
      } catch (err: any) {
        setError(err?.body?.message || err?.message || 'Error al crear el tipo de producto');
      } finally {
        setLoading(false);
      }
    },
    [formData, resetForm],
  );

  const handleClose = useCallback(
    (onClose: () => void) => {
      resetForm();
      onClose();
    },
    [resetForm],
  );

  // Validar si el formulario es válido (nombre es requerido)
  const isFormValid = useMemo(() => {
    return formData.nombre.trim().length > 0;
  }, [formData.nombre]);

  return {
    formData,
    loading,
    error,
    isFormValid,
    handleFormChange,
    handleSave,
    handleClose,
    resetForm,
  };
};
